import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getContactMessages,
  type ContactItem,
  type ContactStatus,
} from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";
import { cn } from "@admin/lib/utils";

function statusClass(status: ContactStatus): string {
  return status === "resolved"
    ? "bg-ok/20 text-ok"
    : "bg-warn/20 text-warn";
}

type Filter = "all" | ContactStatus;

export default function ContactPage() {
  const [items, setItems] = useState<ContactItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<Filter>("all");
  const [err, setErr] = useState<string | null>(null);
  const limit = 50;

  useEffect(() => {
    setErr(null);
    getContactMessages(limit, offset, filter === "all" ? undefined : filter)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch(() => setErr("Could not load contact messages"));
  }, [offset, filter]);

  const setFilterAndReset = (next: Filter) => {
    setFilter(next);
    setOffset(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Contact ({total})</h1>
        <div className="flex gap-2">
          {(["all", "pending", "resolved"] as const).map((f) => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterAndReset(f)}
            >
              {f === "all" ? "All" : f === "pending" ? "Pending" : "Resolved"}
            </Button>
          ))}
        </div>
      </div>
      {err && <p className="text-danger">{err}</p>}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-muted-fg">
            <tr>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Locale</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Preview</th>
              <th className="px-4 py-3 font-medium">Received</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link to={`/contact/${row.id}`} className="text-accent hover:underline">
                    {row.email}
                  </Link>
                </td>
                <td className="px-4 py-3">{row.name ?? "-"}</td>
                <td className="px-4 py-3 uppercase">{row.locale}</td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                      statusClass(row.status),
                    )}
                  >
                    {row.status}
                  </span>
                </td>
                <td className="max-w-xs truncate px-4 py-3 text-muted-fg">
                  {row.messagePreview}
                </td>
                <td className="px-4 py-3 text-muted-fg">{formatDate(row.createdAt)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-fg">
                  No messages yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <span className="text-sm text-muted-fg">
          {offset + 1}-{Math.min(offset + limit, total)} of {total}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={offset + limit >= total}
          onClick={() => setOffset(offset + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
